import React, { useEffect, useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import { Button, Checkbox, Popover, Radio, Result, Select, Space, Tooltip } from 'antd';
import { AreaChartOutlined, BarChartOutlined, CalendarOutlined, DashboardOutlined, FontSizeOutlined, LineChartOutlined, MergeOutlined, PercentageOutlined, PieChartOutlined, RedoOutlined, RiseOutlined, RotateLeftOutlined, TableOutlined, TagOutlined } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import Color from 'color';

import { useQuery } from 'hooks/apollo';

import metrics from 'shared/constants/metrics';
import dimensions from 'shared/constants/dimensions';
import { durationFormat } from 'shared/utils/formatter';
import { calculateDateRanges } from 'shared/constants/date';
import { dayjs } from 'shared/utils/date';
import { getColorMix, STATIC_SEGMENT_COLOR } from 'shared/constants/colors';
import { combineQueries, getBaseQuery, getDateQuery, isQueryEmpty } from 'shared/utils/queryBuilder';
import { dimensionSupportsIgnoreCasing, dimensionIgnoresCasing, withIgnoreDimensionCasing } from 'shared/utils/ignoreDimensionCasing';

import ErrorBoundary from 'components/ErrorBoundary';
import StackedBarChartIcon from 'components/Icons/StackedBarChartIcon';
import { getMetricLabel } from 'components/MetricLabel';
import DimensionLabel from 'components/DimensionLabel';
import useFormatMessage from 'hooks/useFormatMessage';
import Loading from 'components/Loading';
import CenterContent from 'components/CenterContent';
import useUser from 'hooks/useUser';
import { MatomoGoalNamesProvider } from 'hooks/useMatomoGoalNames';
import { WebsiteCurrencyProvider } from 'hooks/useWebsiteCurrency';
import ResultStats from './ResultStats';
import BarChartResult from './BarChartResult';
import PieChartResult from './PieChartResult';
import TableResult from './TableResult';
import NestedTableResult from './NestedTableResult';
import WidgetResult from './WidgetResult';
import LineChartResult from './LineChartResult';
import StackedBarChartResult from './StackedBarChartResult';

import messages from './messages';
import { getDimensionFilters, getQueryDimensions } from './nestedTreePlan';
import { getSwitchableDimensions } from './utils';
import './results.less';

const initialSettings = {
  type: 'table',
  dateComparisonsAsSegments: false,
  showChangeOverTime: true,
  ignoreDimensionCasing: false,
};

const RESULT_QUERY = gql`
  query CustomResult($input: ResultsInput!) {
    results(input: $input) {
      segmentId
      data
      totals
      statistics {
        elapsed
        rowsRead
        bytesRead
        rows
        rowsBeforeLimitAtLeast
        transferred
      }
    }
  }
`;

const presentationTypes = {
  table: {
    icon: <TableOutlined />,
    label: <FormattedMessage {...messages.table} />,
    component: TableResult,
    toolbox: {
      changeOverTime: true,
      dateComparisonsAsSegments: true,
      shareOfTotal: true,
      ignoreDimensionCasing: true,
    },
    metricSelector: false,
  },
  nestedTable: {
    icon: <MergeOutlined />,
    label: <FormattedMessage {...messages.nestedTable} />,
    component: NestedTableResult,
    switchType: false,
    toolbox: {
      changeOverTime: true,
      shareOfTotal: true,
      ignoreDimensionCasing: true,
    },
    metricSelector: false,
  },
  barChart: {
    icon: <BarChartOutlined />,
    label: <FormattedMessage {...messages.barChart} />,
    component: BarChartResult,
    toolbox: {
      verticalBars: true,
      toggleLabels: true,
      shareOfTotal: true,
      ignoreDimensionCasing: true,
    },
    requiresDimensions: true,
    dateComparisonsAsSegments: true,
  },
  stackedBarChart: {
    icon: <StackedBarChartIcon />,
    label: <FormattedMessage {...messages.stackedBarChart} />,
    component: StackedBarChartResult,
    toolbox: {
      verticalBars: true,
      toggleLabels: true,
      shareOfTotal: true,
      ignoreDimensionCasing: true,
    },
    requiresDimensions: true,
    dateComparisonsAsSegments: true,
  },
  pieChart: {
    icon: <PieChartOutlined />,
    label: <FormattedMessage {...messages.pieChart} />,
    component: PieChartResult,
    toolbox: {
      toggleLabels: true,
      shareOfTotal: true,
      ignoreDimensionCasing: true,
    },
    requiresDimensions: true,
    dateComparisonsAsSegments: true,
    hideTitle: true,
  },
  lineChart: {
    icon: <LineChartOutlined />,
    label: <FormattedMessage {...messages.lineChart} />,
    component: LineChartResult,
    toolbox: {
      dateComparisonsAsSegments: true,
      shareOfTotal: true,
      ignoreDimensionCasing: true,
    },
    trend: true,
  },
  areaChart: {
    icon: <AreaChartOutlined />,
    label: <FormattedMessage {...messages.areaChart} />,
    component: LineChartResult,
    toolbox: {
      dateComparisonsAsSegments: true,
      shareOfTotal: true,
      ignoreDimensionCasing: true,
    },
    trend: true,
    requiresDimensions: true,
    props: {
      area: true,
    },
  },
  widget: {
    icon: <DashboardOutlined />,
    label: <FormattedMessage {...messages.widget} />,
    component: WidgetResult,
    // requiresDimensions: false,
    hideTitle: true,
    metricSelector: false,
  },
};

const PresentationTypeSwitcher = ({ presentationType, setPresentationType, dimensionCount, hasLifetimeConversionDates }) => (
  <Radio.Group buttonStyle="solid" value={presentationType} onChange={e => setPresentationType(e.target.value)}>
    {Object.entries(presentationTypes).filter(([key, value]) => (
      (value.switchType !== false
        && !((key === 'lineChart' || key === 'areaChart') && hasLifetimeConversionDates)
        && ((value.requiresDimensions === true && dimensionCount > 0) || (value.requiresDimensions === false && dimensionCount === 0) || value.requiresDimensions === undefined))
    )).map(([key, value]) => (
      <Tooltip key={key} placement="topLeft" title={value.label}>
        <Radio.Button value={key}>
          {value.icon}
        </Radio.Button>
      </Tooltip>
    ))}
  </Radio.Group>
);

const PresentationSettings = ({ settings, defaultSettings, onChange, mainSegment, dimensionCount, resultDimensions, defaultResultDimensions, presentationConfig, hasLifetimeConversionDates }) => {
  const [casingMenuOpen, setCasingMenuOpen] = useState(false);
  const [draftCasingOverrides, setDraftCasingOverrides] = useState();
  const draftDimensions = withIgnoreDimensionCasing(defaultResultDimensions, false, draftCasingOverrides);
  const { changeOverTime, dateComparisonsAsSegments, verticalBars, toggleLabels, shareOfTotal, ignoreDimensionCasing } = presentationConfig.toolbox || {};
  return (
    <Space.Compact>
      {changeOverTime && !hasLifetimeConversionDates && mainSegment.dateRangeSegments?.length > 0 && (
        <Tooltip title="Show change from previous periods">
          <Button
            icon={<RiseOutlined />}
            onClick={() => onChange({ ...settings, showChangeOverTime: !settings.showChangeOverTime })}
            type={settings.showChangeOverTime ? 'primary' : 'default'}
          />
        </Tooltip>
      )}
      {dateComparisonsAsSegments && !hasLifetimeConversionDates && mainSegment.dateRangeSegments?.length > 0 && (
        <Tooltip title="Show date comparison as individual segments">
          <Button
            icon={<CalendarOutlined />}
            onClick={() => onChange({ ...settings, dateComparisonsAsSegments: !settings.dateComparisonsAsSegments })}
            type={settings.dateComparisonsAsSegments ? 'primary' : 'default'}
          />
        </Tooltip>
      )}
      {verticalBars && (
        <Tooltip title="Switch axis">
          <Button
            icon={<RotateLeftOutlined />}
            onClick={() => onChange({ ...settings, verticalBars: !settings.verticalBars })}
          />
        </Tooltip>
      )}
      {!defaultSettings.hideLabels && toggleLabels && (
        <Tooltip title="Toggle labels">
          <Button
            icon={<TagOutlined />}
            onClick={() => onChange({ ...settings, hideLabels: !settings.hideLabels })}
            type={!settings.hideLabels ? 'primary' : 'default'}
          />
        </Tooltip>
      )}
      {shareOfTotal && dimensionCount > 0 && (
        <Tooltip title="Share of total">
          <Button
            icon={<PercentageOutlined />}
            onClick={() => onChange({ ...settings, shareOfTotal: !settings.shareOfTotal })}
            type={settings.shareOfTotal ? 'primary' : 'default'}
          />
        </Tooltip>
      )}
      {ignoreDimensionCasing && dimensionCount > 0 && (
        <Popover
          title="Ignore casing"
          trigger="click"
          placement="topRight"
          open={casingMenuOpen}
          onOpenChange={(open) => {
            if (open) setDraftCasingOverrides(settings.dimensionCasingOverrides);
            setCasingMenuOpen(open);
          }}
          content={(
            <Space direction="vertical">
              {draftDimensions.map(dimension => (
                <Checkbox
                  key={dimension.name}
                  checked={dimensionSupportsIgnoreCasing(dimension) && dimensionIgnoresCasing(dimension)}
                  disabled={!dimensionSupportsIgnoreCasing(dimension)}
                  onChange={event => setDraftCasingOverrides({
                    ...draftCasingOverrides,
                    [dimension.name]: event.target.checked,
                  })}
                >
                  <DimensionLabel dimension={dimension.name} label={dimension.label} lineBreaks={false} />
                </Checkbox>
              ))}
              <Space>
                <Button
                  disabled={!Object.keys(draftCasingOverrides || {}).length && !Object.keys(settings.dimensionCasingOverrides || {}).length}
                  onClick={() => {
                    setDraftCasingOverrides(undefined);
                    onChange({ ...settings, dimensionCasingOverrides: undefined });
                    setCasingMenuOpen(false);
                  }}
                >
                  Reset
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    onChange({ ...settings, dimensionCasingOverrides: draftCasingOverrides });
                    setCasingMenuOpen(false);
                  }}
                >
                  Apply
                </Button>
              </Space>
            </Space>
          )}
        >
          <Button
            aria-label="Ignore casing"
            icon={<FontSizeOutlined />}
            type={resultDimensions.some(d => dimensionSupportsIgnoreCasing(d) && dimensionIgnoresCasing(d)) ? 'primary' : 'default'}
          />
        </Popover>
      )}
    </Space.Compact>
  );
};

const getResultsConfig = ({ resultsConfig, settings }) => ({
  ...resultsConfig,
  settings,
  metrics: resultsConfig?.metrics?.filter(m => metrics[m.metric]).map(m => ({
    ...metrics[m.metric],
    ...m,
    name: m.metric,
  })) || [],
  dimensions: withIgnoreDimensionCasing(
    resultsConfig?.dimensions?.filter(d => dimensions[d.dimension]).map(d => ({
      ...dimensions[d.dimension],
      ...d,
      name: d.dimension,
    })) || [],
    settings?.ignoreDimensionCasing,
    settings?.dimensionCasingOverrides,
  ),
});

function getDateComparisonSegments(comparisonDateRanges, segment, dateRangeConfig) {
  return comparisonDateRanges.map((dateRange, index) => ({
    ...segment,
    dateRange,
    dateRangeConfig,
    id: `${segment.id}-date-comparison-${index}`,
    color: getColorMix(
      Color,
      segment.color,
      '#fff',
      index + 1,
      0,
      comparisonDateRanges.length + 1,
    ),
    segmentColor: segment.color,
    isDateComparisonSegment: true,
  }));
}

function getSegments({ filter, dateComparisonsAsSegments, hasUniqueDimensions, viewDate }) {
  const mainSegment = filter?.allEnabled ? {
    id: 'all',
    title: 'All',
    color: STATIC_SEGMENT_COLOR.dark,
    enabled: true,
    dateRangeConfig: filter.dateRangeConfig,
  } : filter?.segments.find(s => s.enabled);

  if (!mainSegment) {
    return {};
  }

  const mainDateRangeConfig = mainSegment?.dateRangeConfig || filter.dateRangeConfig;
  const [mainDateRange, ...mainComparisonDateRanges] = calculateDateRanges(dayjs, mainDateRangeConfig, 'Europe/Stockholm', viewDate);
  const comparisonSegments = filter.segments?.filter(s => s.enabled && s.id !== mainSegment?.id) || [];

  const data = {
    mainSegment: {
      ...mainSegment,
      segmentColor: mainSegment.color,
      dateRange: mainDateRange,
      dateRangeConfig: mainDateRangeConfig,
      dateRangeSegments: hasUniqueDimensions ? [] : getDateComparisonSegments(mainComparisonDateRanges, mainSegment, mainDateRangeConfig),
    },
    comparisonSegments: comparisonSegments.map(segment => {
      let dateRangeConfig = mainDateRangeConfig;
      let dateRange = mainDateRange;
      let comparisonDateRanges = mainComparisonDateRanges;
      let customDateRange = false;

      if (segment.dateRangeConfig) {
        [dateRange, ...comparisonDateRanges] = calculateDateRanges(dayjs, segment.dateRangeConfig, 'Europe/Stockholm', viewDate);
        dateRangeConfig = segment.dateRangeConfig;
        customDateRange = true;
      }

      return {
        ...segment,
        segmentColor: segment.color,
        dateRange,
        dateRangeConfig,
        dateRangeSegments: hasUniqueDimensions ? [] : getDateComparisonSegments(comparisonDateRanges, segment, dateRangeConfig),
        customDateRange,
        isComparisonSegment: true,
      };
    }),
  };

  data.segments = [
    data.mainSegment,
    ...dateComparisonsAsSegments ? data.mainSegment.dateRangeSegments : [],
    ...data.comparisonSegments,
    ...(dateComparisonsAsSegments ? data.comparisonSegments.flatMap(s => s.dateRangeSegments) : []),
  ];

  return data;
}

const getSegmentInput = (segment) => ({
  id: segment.id,
  query: segment.query,
  dateQuery: getDateQuery(segment),
});

const getQueryInput = ({ customerId, resultsConfig, dataSourceIds, websiteBaseUrls, mainSegment, comparisonSegments = [], trend, nestedTable, switchDimensions, activeDimension, contextFilter }) => {
  if (!customerId || !resultsConfig || !mainSegment) {
    return null;
  }

  const queryDimensions = getQueryDimensions({
    dimensions: resultsConfig.dimensions,
    nestedTable,
    switchDimensions,
    activeDimension,
  });

  return {
    input: {
      metrics: resultsConfig.metrics,
      dimensions: queryDimensions,
      baseQuery: combineQueries(
        getBaseQuery({ customerId, dataSourceIds, websiteBaseUrls }),
        ...getDimensionFilters(queryDimensions),
      ),
      filterQuery: isQueryEmpty(resultsConfig.filter) ? undefined : resultsConfig.filter,
      contextQuery: isQueryEmpty(contextFilter) ? undefined : contextFilter,
      mainSegment: mainSegment ? getSegmentInput(mainSegment) : null,
      comparisonSegments: [
        ...(mainSegment?.dateRangeSegments?.map(getSegmentInput) || []),
        ...comparisonSegments.map(getSegmentInput),
        ...(comparisonSegments.map(s => s.dateRangeSegments?.map(getSegmentInput)).flat() || []),
      ],
      rowLimit: resultsConfig.settings?.rowLimit,
      timeGranularity: trend ? mainSegment.dateRange.periods[0].period.toUpperCase() : undefined,
    },
  };
};

const resolveUrls = (websiteBaseUrls, websites = []) => (websiteBaseUrls ? Array.from(websiteBaseUrls?.reduce((acc, url) => {
  for (const website of websites) {
    if (url === 'production') {
      acc.add(website.url);
      website.additionalLanguages?.forEach(l => acc.add(l.url));
    }
    if (url === 'testing') {
      website.additionalUrls?.forEach(u => acc.add(u));
    }
    if (url === website.id) {
      acc.add(website.url);
    }
    const additionalLanguage = website.additionalLanguages?.find(l => l.id === url);
    if (additionalLanguage) {
      acc.add(additionalLanguage.url);
    }
    const additionalUrl = website.additionalUrls?.find(u => u === url);
    if (additionalUrl) {
      acc.add(additionalUrl);
    }
  }
  return acc;
}, new Set())) : []);

const SegmentsResult = ({ richText, resultsConfig, filter, contextFilter, width, contentRef, customerId, dataSourceIds, websites, viewDate, skipCurrencyQuery }) => {
  const $t = useFormatMessage();
  const [settings, setSettings] = useState({
    ...initialSettings,
    ...(resultsConfig?.settings || {}),
  });
  const [showStats, setShowStats] = useState(false);
  const [activeDimensionName, setActiveDimensionName] = useState(null);
  const { isSuperUser } = useUser();
  const presentationConfig = presentationTypes[settings.type] || presentationTypes.table;
  const defaultResultDimensions = getResultsConfig({ resultsConfig, settings: { ...settings, dimensionCasingOverrides: undefined } }).dimensions;
  const resolvedResultsConfig = getResultsConfig({ resultsConfig, settings });
  const switchableDimensions = getSwitchableDimensions(resolvedResultsConfig.dimensions);
  const switchDimensions = settings.type === 'table'
    && settings.switchDimensions
    && switchableDimensions.length > 1;
  const activeDimension = switchDimensions
    ? switchableDimensions.find(d => d.name === activeDimensionName) || switchableDimensions[0]
    : null;
  const displayResultsConfig = switchDimensions && activeDimension
    ? { ...resolvedResultsConfig, dimensions: [activeDimension] }
    : resolvedResultsConfig;
  const hasLifetimeConversionDates = resolvedResultsConfig.metrics.some(m => m.excludeDateFilter);
  const hasUniqueDimensions = resolvedResultsConfig.dimensions.some(d => d.unique);
  const dateComparisonsAsSegments = presentationConfig.toolbox?.dateComparisonsAsSegments ? settings.dateComparisonsAsSegments : presentationConfig.dateComparisonsAsSegments;

  const { segments, mainSegment, comparisonSegments } = useMemo(() => getSegments({
    filter,
    dateComparisonsAsSegments,
    hasUniqueDimensions,
    viewDate,
  }), [filter, dateComparisonsAsSegments, hasUniqueDimensions, viewDate]);

  const variables = getQueryInput({
    customerId,
    dataSourceIds,
    websiteBaseUrls: resolveUrls(filter?.websiteBaseUrls, websites),
    resultsConfig: resolvedResultsConfig,
    contextFilter,
    mainSegment,
    comparisonSegments,
    trend: presentationConfig?.trend && !hasLifetimeConversionDates,
    nestedTable: settings.type === 'nestedTable',
    switchDimensions,
    activeDimension,
  });

  const { data, loading, fetching, refetch } = useQuery(RESULT_QUERY, {
    variables,
    skip: !variables,
  });

  const [selectedMetric, setSelectedMetric] = useState(resolvedResultsConfig?.metrics[0]?.name);

  useEffect(() => {
    if (resultsConfig?.settings) {
      setSettings({
        ...settings,
        ...resultsConfig.settings,
      });
    }
  }, [resultsConfig]);

  useEffect(() => {
    if (!switchDimensions) {
      setActiveDimensionName(null);
      return;
    }
    if (!switchableDimensions.some(d => d.name === activeDimensionName)) {
      setActiveDimensionName(switchableDimensions[0]?.name ?? null);
    }
  }, [switchDimensions, switchableDimensions, activeDimensionName]);

  const ResultComponent = presentationConfig?.component || TableResult;

  const isEmptyResult = data?.results?.length === 0 || !data?.results?.[0]?.data?.length;
  const dimensionCount = resolvedResultsConfig?.dimensions?.length || 0;
  const elapsed = data?.results?.reduce((total, r) => total + (r.statistics?.elapsed || 0), 0);

  return (
    <WebsiteCurrencyProvider
      customerId={customerId}
      dataSourceIds={filter?.dataSources?.map(ds => ds.id)}
      skip={skipCurrencyQuery}
    >
      <>
        <ErrorBoundary reset={settings.type}>
          <div ref={contentRef} style={{ backgroundColor: 'white' }}>
            {loading ? (
              <Loading fullsize={false} style={{ padding: '2rem' }} />
            ) : (
              <>
                {richText && (
                  <div
                    className="dashboard-text-content"
                    dangerouslySetInnerHTML={{ __html: richText }}
                  />
                )}
                {isEmptyResult ? (
                  <Result status="warning" subTitle="No data, try another date range." />
                ) : (
                  <>
                    {presentationConfig.metricSelector !== false && resolvedResultsConfig.dimensions.length > 0 && (
                      <CenterContent style={{ margin: 8 }}>
                        <Select options={resolvedResultsConfig.metrics.map(m => ({ label: getMetricLabel({ $t, name: m.name }), value: m.name }))} value={selectedMetric} onChange={setSelectedMetric} />
                      </CenterContent>
                    )}
                    <MatomoGoalNamesProvider results={data?.results} dimensions={displayResultsConfig.dimensions}>
                      <ResultComponent
                        results={data?.results}
                        config={displayResultsConfig}
                        segments={segments}
                        mainSegment={mainSegment}
                        comparisonSegments={comparisonSegments}
                        width={width}
                        selectedMetric={selectedMetric}
                        variables={variables}
                        switchableDimensions={switchDimensions ? switchableDimensions : undefined}
                        activeDimension={activeDimension?.name}
                        onActiveDimensionChange={setActiveDimensionName}
                        {...presentationConfig?.props}
                      />
                    </MatomoGoalNamesProvider>
                  </>
                )}
              </>
            )}
          </div>
        </ErrorBoundary>
        <div className="dashboard__content__footer">
          <Space>
            {!loading && !isEmptyResult && width > 600 && (
              <>
                {!settings.hidePresentationSwitcher && presentationConfig.switchType !== false && !hasUniqueDimensions && <PresentationTypeSwitcher presentationType={settings.type} setPresentationType={t => setSettings({ ...settings, type: t })} dimensionCount={dimensionCount} hasLifetimeConversionDates={hasLifetimeConversionDates} />}
                {!settings.hidePresentationSettings && <PresentationSettings settings={settings} defaultSettings={resultsConfig.settings} onChange={setSettings} mainSegment={mainSegment} dimensionCount={dimensionCount} resultDimensions={resolvedResultsConfig.dimensions} defaultResultDimensions={defaultResultDimensions} presentationConfig={presentationConfig} hasLifetimeConversionDates={hasLifetimeConversionDates} />}
              </>
            )}
          </Space>

          {isSuperUser && (
            <Space.Compact>
              <Button onClick={() => refetch()} icon={<RedoOutlined />} loading={fetching} />
              <Button onClick={() => setShowStats(!showStats)}>
                {durationFormat(elapsed * 1000)}
              </Button>
            </Space.Compact>
          )}
        </div>
        {showStats && <ResultStats results={data?.results} loading={loading} segments={segments} />}
      </>
    </WebsiteCurrencyProvider>
  );
};

export default SegmentsResult;
